import { create } from 'zustand';
import type { GisLayer, GisSceneState } from '@dtwin/shared';
import { DEFAULT_GIS_CAMERA, generateId } from '@dtwin/shared';
import useSceneStore from './sceneStore';

// 2026-09-27（审计 FE-H08）：GIS 图层/围栏/热力图随场景保存 → 变更时标记未保存（提示用户保存）
// 防御性可选调用：部分单测 mock sceneStore 未提供 getState
const markDirty = () => (useSceneStore as any).getState?.()?.setDirty?.(true);

export interface GeoFence {
  id: string;
  name: string;
  points: { lon: number; lat: number }[];
  color: string;
  visible: boolean;
  label?: string;
}

export interface HeatmapPoint {
  lon: number;
  lat: number;
  value: number;
}

export interface HeatmapData {
  id: string;
  name: string;
  points: HeatmapPoint[];
  visible: boolean;
  radius: number;
  maxOpacity: number;
  gradient: Record<number, string>;
}

export type DrawingMode = 'geoFence' | 'heatmap' | null;

interface DrawingState {
  mode: DrawingMode;
  points: { lon: number; lat: number }[];
  fenceName: string;
  fenceColor: string;
  heatmapName: string;
  heatmapRadius: number;
}

interface GisStoreState {
  gisEnabled: boolean;
  gisState: GisSceneState;
  geoFences: GeoFence[];
  heatmaps: HeatmapData[];
  cursorPosition: { lon: number; lat: number; height: number } | null;
  drawing: DrawingState;
  toggleGis: () => void;
  setGisEnabled: (enabled: boolean) => void;
  setLayers: (layers: GisLayer[]) => void;
  addLayer: (layer: GisLayer) => void;
  removeLayer: (id: string) => void;
  updateLayer: (id: string, updates: Partial<GisLayer>) => void;
  setCamera: (camera: Partial<GisSceneState['camera']>) => void;
  setViewerMode: (mode: GisSceneState['viewerMode']) => void;
  setCursorPosition: (pos: { lon: number; lat: number; height: number } | null) => void;
  startDrawing: (mode: DrawingMode, options?: Partial<Pick<DrawingState, 'fenceName' | 'fenceColor' | 'heatmapName' | 'heatmapRadius'>>) => void;
  addDrawingPoint: (lon: number, lat: number) => void;
  finishDrawing: () => void;
  cancelDrawing: () => void;
  addGeoFence: (fence: Omit<GeoFence, 'id'>) => void;
  updateGeoFence: (id: string, updates: Partial<GeoFence>) => void;
  removeGeoFence: (id: string) => void;
  toggleGeoFence: (id: string) => void;
  addHeatmap: (heatmap: Omit<HeatmapData, 'id'>) => void;
  updateHeatmap: (id: string, updates: Partial<HeatmapData>) => void;
  removeHeatmap: (id: string) => void;
  toggleHeatmap: (id: string) => void;
  /** 2026-09-27（审计 FE-H08 修复）：从场景配置恢复（图层/围栏/热力图/视图/相机/设置） */
  restore: (payload: {
    gisState?: Partial<GisSceneState>;
    geoFences?: GeoFence[];
    heatmaps?: HeatmapData[];
  }) => void;
}

const defaultGradient: Record<number, string> = {
  0.0: 'blue',
  0.25: 'cyan',
  0.5: 'lime',
  0.75: 'yellow',
  1.0: 'red',
};

const useGisStore = create<GisStoreState>((set, get) => ({
  gisEnabled: false,
  gisState: {
    viewerMode: '3d',
    enableLighting: true,
    showTerrain: true,
    showSkyAtmosphere: true,
    depthTestAgainstTerrain: false,
    layers: [],
    camera: { ...DEFAULT_GIS_CAMERA },
  },
  geoFences: [],
  heatmaps: [],
  cursorPosition: null,
  drawing: { mode: null, points: [], fenceName: '', fenceColor: '#ff4d4f', heatmapName: '', heatmapRadius: 20 },

  toggleGis: () => set((s) => ({ gisEnabled: !s.gisEnabled })),
  setGisEnabled: (enabled) => set({ gisEnabled: enabled }),

  setLayers: (layers) => {
    markDirty();
    set((s) => ({ gisState: { ...s.gisState, layers } }));
  },

  addLayer: (layer) => {
    markDirty();
    set((s) => ({
      gisState: { ...s.gisState, layers: [...s.gisState.layers, layer] },
    }));
  },

  removeLayer: (id) => {
    markDirty();
    set((s) => ({
      gisState: {
        ...s.gisState,
        layers: s.gisState.layers.filter((l) => l.id !== id),
      },
    }));
  },

  updateLayer: (id, updates) => {
    markDirty();
    set((s) => ({
      gisState: {
        ...s.gisState,
        layers: s.gisState.layers.map((l) =>
          l.id === id ? { ...l, ...updates } : l,
        ),
      },
    }));
  },

  // 相机随视图移动频繁变化，不标记未保存（保存时随配置一并落库）
  setCamera: (camera) =>
    set((s) => ({
      gisState: { ...s.gisState, camera: { ...s.gisState.camera, ...camera } },
    })),

  setViewerMode: (mode) => {
    markDirty();
    set((s) => ({ gisState: { ...s.gisState, viewerMode: mode } }));
  },

  setCursorPosition: (pos) => set({ cursorPosition: pos }),

  startDrawing: (mode, options) =>
    set((s) => ({
      drawing: {
        mode,
        points: [],
        fenceName: options?.fenceName || `电子围栏 ${s.geoFences.length + 1}`,
        fenceColor: options?.fenceColor || '#ff4d4f',
        heatmapName: options?.heatmapName || `热力图 ${s.heatmaps.length + 1}`,
        heatmapRadius: options?.heatmapRadius || 20,
      },
    })),

  addDrawingPoint: (lon, lat) =>
    set((s) => ({
      drawing: { ...s.drawing, points: [...s.drawing.points, { lon, lat }] },
    })),

  finishDrawing: () => {
    const d0 = get().drawing;
    const willCreate =
      (d0.mode === 'geoFence' && d0.points.length >= 3) ||
      (d0.mode === 'heatmap' && d0.points.length > 0);
    if (willCreate) markDirty();
    set((s) => {
      const d = s.drawing;
      if (d.mode === 'geoFence' && d.points.length >= 3) {
        return {
          geoFences: [...s.geoFences, {
            id: generateId(),
            name: d.fenceName,
            points: d.points,
            color: d.fenceColor,
            visible: true,
          }],
          drawing: { ...d, mode: null, points: [] },
        };
      }
      if (d.mode === 'heatmap' && d.points.length > 0) {
        return {
          heatmaps: [...s.heatmaps, {
            id: generateId(),
            name: d.heatmapName,
            points: d.points.map((p) => ({ ...p, value: Math.random() })),
            visible: true,
            radius: d.heatmapRadius,
            maxOpacity: 0.8,
            gradient: { 0.0: 'blue', 0.25: 'cyan', 0.5: 'lime', 0.75: 'yellow', 1.0: 'red' },
          }],
          drawing: { ...d, mode: null, points: [] },
        };
      }
      return { drawing: { ...d, mode: null, points: [] } };
    });
  },

  cancelDrawing: () =>
    set((s) => ({
      drawing: { ...s.drawing, mode: null, points: [] },
    })),

  addGeoFence: (fence) => {
    markDirty();
    set((s) => ({
      geoFences: [...s.geoFences, { ...fence, id: generateId() }],
    }));
  },

  updateGeoFence: (id, updates) => {
    markDirty();
    set((s) => ({
      geoFences: s.geoFences.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    }));
  },

  removeGeoFence: (id) => {
    markDirty();
    set((s) => ({
      geoFences: s.geoFences.filter((f) => f.id !== id),
    }));
  },

  toggleGeoFence: (id) => {
    markDirty();
    set((s) => ({
      geoFences: s.geoFences.map((f) =>
        f.id === id ? { ...f, visible: !f.visible } : f,
      ),
    }));
  },

  addHeatmap: (heatmap) => {
    markDirty();
    set((s) => ({
      heatmaps: [...s.heatmaps, { ...heatmap, id: generateId() }],
    }));
  },

  updateHeatmap: (id, updates) => {
    markDirty();
    set((s) => ({
      heatmaps: s.heatmaps.map((h) => (h.id === id ? { ...h, ...updates } : h)),
    }));
  },

  removeHeatmap: (id) => {
    markDirty();
    set((s) => ({
      heatmaps: s.heatmaps.filter((h) => h.id !== id),
    }));
  },

  toggleHeatmap: (id) => {
    markDirty();
    set((s) => ({
      heatmaps: s.heatmaps.map((h) =>
        h.id === id ? { ...h, visible: !h.visible } : h,
      ),
    }));
  },

  /** 2026-09-27（审计 FE-H08 修复）：从场景配置恢复 GIS 状态 */
  restore: (payload) =>
    set((s) => ({
      gisState: payload.gisState ? { ...s.gisState, ...payload.gisState } : s.gisState,
      geoFences: payload.geoFences ?? s.geoFences,
      heatmaps: payload.heatmaps ?? s.heatmaps,
    })),
}));

// ============================================
// 调试/测试入口（同 window.__deviceDataStore 先例）：
// E2E 可经此驱动绘制流程（addDrawingPoint/finishDrawing），绕过 Cesium 球面拾取（headless 不稳）
// ============================================
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__gisStore = useGisStore;
}

export default useGisStore;
